import { resolveHomeHeaderDisplayName } from './homeHeaderDisplayName';
import { isPhoneLiteRegisterSyntheticEmail } from './profileLitePlaceholders';

/** True when the label is probably an e-mail address, not a person's name. */
export function isEmailLikeDisplayLabel(label: string | null | undefined): boolean {
  const trimmed = label?.trim();
  if (!trimmed) {
    return false;
  }
  return trimmed.includes('@') && !trimmed.includes(' ');
}

/** True when the profile API name should replace the login-time label (often an email). */
export function shouldSyncUserNameFromProfile(
  storedUserName: string | null | undefined,
  profileDisplayName: string,
): boolean {
  const profile = profileDisplayName.trim();
  if (!profile) {
    return false;
  }
  const stored = storedUserName?.trim() || '';
  if (profile === stored) {
    return false;
  }
  if (!stored) {
    return true;
  }
  if (stored.includes('@') && !profile.includes('@')) {
    return true;
  }
  if (profile.includes(' ') && !stored.includes(' ')) {
    return true;
  }
  if (looksLikeMojibakeHebrewLabel(stored) && !looksLikeMojibakeHebrewLabel(profile)) {
    return true;
  }
  if (profile !== stored) {
    return true;
  }
  return false;
}

/** Latin-1 misread of UTF-8 Hebrew often contains x™ / xœ patterns. */
export function looksLikeMojibakeHebrewLabel(label: string): boolean {
  return /x[™œŽž¢]x/i.test(label) || /\u00C2[\u0080-\u00BF]/.test(label);
}

export function resolveHeaderDisplayName(
  profileDisplayName: string | null | undefined,
  userName: string | null | undefined,
  cachedNameFromHints?: string | null,
): string | null {
  const fromProfile = profileDisplayName?.trim();
  if (fromProfile) {
    return fromProfile;
  }
  const fromHints = cachedNameFromHints?.trim();
  if (fromHints) {
    return fromHints;
  }
  const stored = userName?.trim();
  if (stored && !isEmailLikeDisplayLabel(stored)) {
    return stored;
  }
  return resolveHomeHeaderDisplayName(userName);
}

function looksLikeUuid(value: string): boolean {
  const compact = value.trim().toLowerCase().replace(/-/g, '');
  return compact.length === 32 && /^[0-9a-f]{32}$/.test(compact);
}

/** JWT sub, synthetic phone inbox, or lite-register placeholders — not a human label. */
export function isUnusableUserFacingLabel(label: string | null | undefined): boolean {
  const trimmed = label?.trim();
  if (!trimmed) {
    return true;
  }
  if (looksLikeUuid(trimmed)) {
    return true;
  }
  if (isPhoneLiteRegisterSyntheticEmail(trimmed)) {
    return true;
  }
  const lower = trimmed.toLowerCase();
  if (lower === 'user' || lower === 'phone') {
    return true;
  }
  if (/^phone\s+user$/i.test(trimmed)) {
    return true;
  }
  return false;
}

/** Compact social-strip chip label — same source as the header, with safe fallbacks. */
export function resolveSocialStripUserLabel(
  displayName: string | null | undefined,
  fallbackLabel: string,
): string {
  const trimmed = displayName?.trim();
  if (!trimmed || isUnusableUserFacingLabel(trimmed)) {
    return fallbackLabel;
  }
  return trimmed;
}
